import { useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';
import { Add, Delete, Edit } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  AgencyQuickReplyDto,
  CreateAgencyQuickReplyDto,
} from '@bandido/shared/dtos';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { WhatsappQuickReplyDialog } from '@frontend/features/whatsapp/components/WhatsappQuickReplyDialog';
import { useCreateQuickReply } from '@frontend/features/whatsapp/hooks/useCreateQuickReply';
import { useDeleteQuickReply } from '@frontend/features/whatsapp/hooks/useDeleteQuickReply';
import { useGetQuickReplies } from '@frontend/features/whatsapp/hooks/useGetQuickReplies';
import { useUpdateQuickReply } from '@frontend/features/whatsapp/hooks/useUpdateQuickReply';

type WhatsappQuickRepliesSectionProps = {
  agencyId: number;
};

const getMutationErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (Array.isArray(message)) {
      return message.join(', ');
    }
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return fallback;
};

const getBodyPreview = (body: string, maxLength = 80) => {
  const normalizedBody = body.replace(/\s+/g, ' ').trim();
  if (normalizedBody.length <= maxLength) {
    return normalizedBody;
  }
  return `${normalizedBody.slice(0, maxLength)}...`;
};

export const WhatsappQuickRepliesSection = ({
  agencyId,
}: WhatsappQuickRepliesSectionProps) => {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const { data, isLoading, isError } = useGetQuickReplies(agencyId);
  const createQuickReply = useCreateQuickReply(agencyId);
  const updateQuickReply = useUpdateQuickReply(agencyId);
  const deleteQuickReply = useDeleteQuickReply(agencyId);

  const [searchText, setSearchText] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedQuickReply, setSelectedQuickReply] = useState<
    AgencyQuickReplyDto | undefined
  >(undefined);
  const [quickReplyToDelete, setQuickReplyToDelete] = useState<
    AgencyQuickReplyDto | undefined
  >(undefined);

  const quickReplies = data || [];
  const filteredQuickReplies = useMemo(() => {
    const searchValue = searchText.trim().toLowerCase();
    if (!searchValue) return quickReplies;

    return quickReplies.filter((quickReply) =>
      `${quickReply.slug} ${quickReply.name} ${quickReply.body}`
        .toLowerCase()
        .includes(searchValue),
    );
  }, [quickReplies, searchText]);

  const isSubmittingDialog =
    createQuickReply.isLoading || updateQuickReply.isLoading;

  const handleCreateClick = () => {
    setSelectedQuickReply(undefined);
    setDialogOpen(true);
  };

  const handleEditClick = (quickReply: AgencyQuickReplyDto) => {
    setSelectedQuickReply(quickReply);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (isSubmittingDialog) return;
    setDialogOpen(false);
    setSelectedQuickReply(undefined);
  };

  const handleSaveQuickReply = async (values: CreateAgencyQuickReplyDto) => {
    try {
      if (selectedQuickReply) {
        await updateQuickReply.mutateAsync({
          quickReplyId: selectedQuickReply.id,
          data: values,
        });
        addToast({
          message: t('whatsapp.settings.quickReplies.messages.updatedSuccess'),
          color: 'success',
        });
      } else {
        await createQuickReply.mutateAsync(values);
        addToast({
          message: t('whatsapp.settings.quickReplies.messages.createdSuccess'),
          color: 'success',
        });
      }

      handleCloseDialog();
    } catch (error) {
      addToast({
        message: getMutationErrorMessage(
          error,
          t('whatsapp.settings.quickReplies.messages.saveError'),
        ),
        color: 'error',
      });
    }
  };

  const handleToggleEnabled = async (
    quickReply: AgencyQuickReplyDto,
    enabled: boolean,
  ) => {
    try {
      await updateQuickReply.mutateAsync({
        quickReplyId: quickReply.id,
        data: { enabled },
      });
    } catch (error) {
      addToast({
        message: getMutationErrorMessage(
          error,
          t('whatsapp.settings.quickReplies.messages.toggleError'),
        ),
        color: 'error',
      });
    }
  };

  const handleDelete = async () => {
    if (!quickReplyToDelete) return;

    try {
      await deleteQuickReply.mutateAsync(quickReplyToDelete.id);
      addToast({
        message: t('whatsapp.settings.quickReplies.messages.deletedSuccess'),
        color: 'success',
      });
      setQuickReplyToDelete(undefined);
    } catch (error) {
      addToast({
        message: getMutationErrorMessage(
          error,
          t('whatsapp.settings.quickReplies.messages.deleteError'),
        ),
        color: 'error',
      });
    }
  };

  const emptyMessage = quickReplies.length
    ? t('whatsapp.settings.quickReplies.emptySearch')
    : t('whatsapp.settings.quickReplies.empty');

  return (
    <Box maxWidth={960} width="100%" display="flex" flexDirection="column" gap={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2">
            {t('whatsapp.settings.quickReplies.title')}
          </Typography>
          <IconButton 
          color="primary"
          onClick={handleCreateClick}
          disabled={isSubmittingDialog}>
          <Add />
          </IconButton>
        </Box>

      </Box>

      <TextField
        label={t('whatsapp.settings.quickReplies.searchLabel')}
        placeholder={t('whatsapp.settings.quickReplies.searchPlaceholder')}
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        fullWidth
        size="small"
      />

      {isLoading ? (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={260}
          sx={{ borderRadius: 2 }}
        />
      ) : isError ? (
        <Typography variant="body2" color="error">
          {t('whatsapp.settings.quickReplies.messages.loadError')}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t('whatsapp.settings.quickReplies.table.slug')}
              </TableCell>
              <TableCell>
                {t('whatsapp.settings.quickReplies.table.name')}
              </TableCell>
              <TableCell>
                {t('whatsapp.settings.quickReplies.table.preview')}
              </TableCell>
              <TableCell align="center">
                {t('whatsapp.settings.quickReplies.table.enabled')}
              </TableCell>
              <TableCell align="right">
                {t('whatsapp.settings.quickReplies.table.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredQuickReplies.map((quickReply) => {
              const preview = getBodyPreview(quickReply.body);

              return (
                <TableRow key={quickReply.id}>
                  <TableCell>{quickReply.slug}</TableCell>
                  <TableCell>{quickReply.name}</TableCell>
                  <TableCell>
                    <Tooltip title={quickReply.body}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          maxWidth: 320,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {preview}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={quickReply.enabled}
                      onChange={(event) =>
                        handleToggleEnabled(quickReply, event.target.checked)
                      }
                      disabled={updateQuickReply.isLoading}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => handleEditClick(quickReply)}
                      disabled={isSubmittingDialog}
                      aria-label={t(
                        'whatsapp.settings.quickReplies.actions.edit',
                      )}
                    >
                      <Edit color="primary" />
                    </IconButton>
                    <IconButton
                      onClick={() => setQuickReplyToDelete(quickReply)}
                      disabled={deleteQuickReply.isLoading}
                      aria-label={t(
                        'whatsapp.settings.quickReplies.actions.delete',
                      )}
                    >
                      <Delete color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}

            {filteredQuickReplies.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <WhatsappQuickReplyDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSaveQuickReply}
        quickReply={selectedQuickReply}
        isSubmitting={isSubmittingDialog}
      />

      <Dialog
        open={Boolean(quickReplyToDelete)}
        onClose={
          deleteQuickReply.isLoading ? undefined : () => setQuickReplyToDelete(undefined)
        }
        disableEscapeKeyDown={deleteQuickReply.isLoading}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {t('whatsapp.settings.quickReplies.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('whatsapp.settings.quickReplies.deleteDialog.description', {
              name: quickReplyToDelete?.name || '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setQuickReplyToDelete(undefined)}
            disabled={deleteQuickReply.isLoading}
          >
            {t('common.cancel')}
          </Button>
          <LoadingButton
            loading={deleteQuickReply.isLoading}
            color="error"
            variant="contained"
            onClick={handleDelete}
          >
            {t('common.delete')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
